<template>
  <section class="layout">
    <!-- 顶部导航栏：固定在顶部 -->
    <Header :isSmallScreen="screenWidth < COLLAPSE_BREAKPOINT" @toggle-sidebar="handleToggleSidebar" :noticeV="noticeV"/>

    <!-- 主体内容区域 -->
    <section class="main-container" :class="{ 'is-collapsed': sidebarCollapsed }">
      <!-- 左侧侧边菜单：固定在左侧 -->
      <Sidebar :collapsed="sidebarCollapsed" />

      <!-- 右侧内容区：页签 + 主体内容 -->
      <section class="content-container">
        <!-- 页签标签栏 -->
        <Tabs />

        <!-- 主体内容区 -->
        <main class="main-content">
          <router-view v-slot="{ Component }">
            <transition
              name="fade-transform"
              mode="out-in"
              style="height: 100%; display: flex; flex-direction: column"
            >
              <!-- 使用 :key 确保每次路由变化都会创建新的 ErrorBoundary 实例 -->
              <ErrorBoundary :key="route.path + '-' + refreshKey">
                <KeepAlive v-if="route.meta.keepAlive">
                  <component :is="Component" :key="refreshKey" />
                </KeepAlive>
                <component v-else :is="Component" :key="refreshKey" />
              </ErrorBoundary>
            </transition>
          </router-view>
        </main>
      </section>
    </section>

    <!-- 公告右下角浮窗 -->
    <transition name="float-animate">
      <div v-if="noticeFloat.show" class="notice-float">
        <div class="float-title">系统公告提醒</div>
        <div class="float-text">
          <p>今日已发布公告：<span class="count-num">{{ noticeFloat.today }}</span> 条</p>
          <p>近7天已发布公告：<span class="count-num">{{ noticeFloat.week }}</span> 条</p>
        </div>
        <div class="btn-row">
          <el-button size="small" type="primary" @click="goNotice">前往查看</el-button>
          <el-button size="small" @click="closeNoticeFloat">关闭</el-button>
        </div>
      </div>
    </transition>
  </section>
</template>

<script setup lang="ts">
defineOptions({
  name: 'Layout',
})
import Header from './Header.vue'
import Sidebar from './Sidebar.vue'
import Tabs from './Tabs.vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import { useTabsStore } from '@/stores/modules/tabs'
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElButton } from 'element-plus'
import { getStorage, removeStorage } from '@/utils/storage'
import { useRouter, useRoute } from 'vue-router'
import { emitter } from '@/utils/mitt'
import dayjs from 'dayjs'
import { getNoticeListApi2 } from '@/api/modules/notice'

const route = useRoute()
const router = useRouter()
const tabsStore = useTabsStore()

// 用于组件重新渲染的 key
const refreshKey = computed(() => {
  if (tabsStore.refreshKey.startsWith(route.path)) {
    return tabsStore.refreshKey
  }
  return route.path
})
const noticeV = ref(false)

// 侧边栏收缩状态
const sidebarCollapsed = ref(false)

// 屏幕宽度监听
const screenWidth = ref(window.innerWidth)

// 响应式收起侧边栏的阈值（单位：px）
const COLLAPSE_BREAKPOINT = 992

// ========== 公告悬浮弹窗状态 ==========
const noticeFloat = ref({
  show: false,
  today: 0,
  week: 0
})

 const fetchNoticeCount = async() =>{
  const todayStr = dayjs().format('YYYY-MM-DD HH:mm:ss')
  const todayEndStr = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss')
  const sevenDayAgo = dayjs().subtract(7, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')

  const todayRes: any = await getNoticeListApi2({
    page: 1,
    page_size: 100,
    status: 2,
    publish_start_time: todayEndStr,
    publish_end_time: todayStr 
  })

  const weekRes: any = await getNoticeListApi2({
    page: 1,
    page_size: 100,
    status: 2,
    publish_start_time: sevenDayAgo,
    publish_end_time: todayStr
  })

  noticeFloat.value.today = todayRes.total
  noticeFloat.value.week = weekRes.total
  noticeFloat.value.show = true
}
 
// 跳转公告页面
const goNotice = () => {
  noticeFloat.value.show = false
  noticeV.value = true
}
const closeNoticeFloat = () => {
  noticeFloat.value.show = false
}

// 监听屏幕宽度变化
const handleResize = () => {
  screenWidth.value = window.innerWidth
  if (screenWidth.value < COLLAPSE_BREAKPOINT) {
    sidebarCollapsed.value = true
  }
}

//登录成功事件回调
const loginSuccessHandler = async () => { 
  await fetchNoticeCount()
  removeStorage('showNoticeFloat')
}

onMounted(async () => {

  handleResize()
  window.addEventListener('resize', handleResize)
  
  emitter.on('loginSuccess', loginSuccessHandler) 
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// 切换侧边栏
const handleToggleSidebar = (collapsed: boolean) => {
  sidebarCollapsed.value = collapsed
}
</script>

<style scoped lang="scss">
.layout {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: var(--el-bg-color-page);

  .main-container {
    display: flex;
    height: calc(100vh - 50px);
    margin-top: 50px;
    transition: margin-left 0.3s ease;

    &.is-collapsed {
      .content-container {
        margin-left: 64px;
      }
    }

    .content-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      margin-left: 200px;
      transition: margin-left 0.3s ease;

      .main-content {
        background-color: #fff;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
      }
    }
  }
}

// 响应式侧边栏收起
@media screen and (max-width: 992px) {
  .layout {
    .main-container {
      &.is-collapsed {
        .content-container {
          margin-left: 64px;
        }
      }
    }
  }
}

// 页面切换动画
.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all 0.3s;
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* 公告右下角悬浮窗 */
.notice-float {
  position: fixed;
  right: 24px;
  bottom: 90px;
  background: #ffffff;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.14);
  z-index: 9998;
  min-width: 370px;

  .float-title {
    font-weight: bold;
    font-size: 16px;
    color: #303133;
    margin-bottom: 10px;
  }
  .float-text p {
    margin:6px 0;
    font-size:14px;
    color:#606266;
  }
  .count-num {
    color:#409eff;
    font-weight: bold;
    font-size:17px;
  }
  .btn-row {
    margin-top:12px;
    display:flex;
    gap:8px;
  }
}
</style>

<style lang="scss">
/* 浮窗弹出动画 */
.float-animate-enter-active,
.float-animate-leave-active {
  transition: all 0.25s ease;
}
.float-animate-enter-from {
  opacity: 0;
  transform: translateY(18px);
}
.float-animate-leave-to {
  opacity: 0;
  transform: translateY(18px);
}
</style>